import type {UiReadinessHandoff} from '@github-ui/ahp-session-client/ui-readiness-handoff'
import {sendCustomMetric} from '@github-ui/stats'

import {createEmitter, type MetricSender} from './browser-outcome-metrics'
import {boundOpenState, metricSurface} from './metric-surface'
import {failed, NO_REASONS, readinessFailureReason, unresolved, type OutcomeReasons} from './outcome-reasons'
import {taskRepositoryScope} from './repository-scope'
import {startTypeFor} from './start-type'
import type {UiReadinessFact, UiReadinessOpen, UiReadinessSurface, UiReadinessTime} from './ui-readiness-contract'

export const HANDOFF_SOURCES = ['dashboard', 'contextual', 'other', 'unknown'] as const
export type HandoffSource = (typeof HANDOFF_SOURCES)[number]
export const HANDOFF_PHASES = ['source_to_copilot', 'copilot_ready', 'copilot_to_task', 'task_ready'] as const
export type HandoffPhase = (typeof HANDOFF_PHASES)[number]
type HandoffNavigation = 'hard' | 'soft' | 'unknown'

const TASK_SURFACES: readonly UiReadinessSurface[] = ['global_task', 'repository_task']

export function handoffSource(source: string | undefined): HandoffSource {
  if (!source) return 'unknown'
  if (source === 'dashboard') return 'dashboard'
  if (source === 'contextual-chat') return 'contextual'
  return 'other'
}

interface Journey {
  readonly openId: string
  readonly source: HandoffSource
  readonly submit: boolean
  readonly navigationType: HandoffNavigation
  readonly sourceToCopilotMs?: number
  readonly claimedAt: UiReadinessTime
  surface: UiReadinessSurface
  taskOpenId?: string
  taskOpenedAt?: number
  taskId?: string
  latestFact?: UiReadinessFact
}

interface HandoffReadinessContext {
  readonly open: Pick<UiReadinessOpen, 'expectedTaskId'>
  readonly userInitiated: boolean
}

/**
 * Follows a One Copilot entry from the source action through `/copilot` to the task page.
 * A submitted entry ends when the task page is input-ready. A prefilled entry waits for the
 * user to send, so it ends when `/copilot` is input-ready.
 */
export function createHandoffJourneyMetrics({
  send = sendCustomMetric,
  reportError = () => undefined,
  pageExitTarget = typeof window === 'undefined' ? undefined : window,
}: {
  send?: MetricSender
  reportError?: () => void
  pageExitTarget?: Pick<Window, 'addEventListener'>
} = {}) {
  const emit = createEmitter(send, reportError)
  let journey: Journey | undefined
  let pageExitInstalled = false

  const tags = (current: Journey | undefined, source: HandoffSource, fact?: UiReadinessFact) => ({
    source,
    navigation_type: current?.navigationType ?? 'unknown',
    repository_scope:
      fact && fact.repository_scope !== 'unknown' ? fact.repository_scope : taskRepositoryScope(fact?.task_id),
    surface: metricSurface(current?.surface ?? 'unknown', fact ? boundOpenState(fact) : 'unknown'),
    start_type: fact ? startTypeFor(fact) : 'unknown',
  })
  const attributedFact = (current: Journey, fact?: UiReadinessFact): UiReadinessFact | undefined => {
    const latest = current.latestFact
    if (!fact) return latest
    if (!latest) return fact
    const taskId = fact.task_id ?? latest.task_id
    const repositoryScope = fact.repository_scope !== 'unknown' ? fact.repository_scope : latest.repository_scope
    return {
      ...fact,
      ...(taskId ? {task_id: taskId} : {}),
      repository_scope: repositoryScope,
    }
  }
  const remember = (current: Journey, fact: UiReadinessFact) => {
    current.latestFact = attributedFact(current, fact)
  }
  const finish = (outcome: 'success' | 'failure' | 'unresolved', reasons: OutcomeReasons, fact?: UiReadinessFact) => {
    const current = journey
    if (!current) return
    journey = undefined
    const attribution = attributedFact(current, fact)
    emit(
      {
        name: 'ONE_COPILOT_WEB_HANDOFF_OUTCOME',
        value: 1,
        tags: {...tags(current, current.source, attribution), outcome, ...reasons},
      },
      attribution,
    )
  }
  const succeed = (fact: UiReadinessFact, phases: Array<[HandoffPhase, number | undefined]>) => {
    const current = journey
    if (!current) return
    const attribution = attributedFact(current, fact) ?? fact
    const sourceToCopilot = current.sourceToCopilotMs
    const destination = fact.monotonic_ms - current.claimedAt.monotonicMs
    if (sourceToCopilot !== undefined && destination >= 0) {
      emit(
        {
          name: 'ONE_COPILOT_WEB_HANDOFF_LATENCY',
          value: sourceToCopilot + destination,
          tags: tags(current, current.source, attribution),
        },
        attribution,
      )
    }
    for (const [phase, value] of [['source_to_copilot', sourceToCopilot], ...phases] as const) {
      if (value === undefined || value < 0) continue
      emit(
        {
          name: 'ONE_COPILOT_WEB_HANDOFF_PHASE_LATENCY',
          value,
          tags: {...tags(current, current.source, attribution), phase},
        },
        attribution,
      )
    }
    finish('success', NO_REASONS, attribution)
  }

  return {
    claimed(seed: UiReadinessHandoff, openId: string, surface: UiReadinessSurface, claimedAt: UiReadinessTime) {
      if (journey) finish('unresolved', unresolved('other'))
      if (!pageExitInstalled && pageExitTarget) {
        pageExitInstalled = true
        pageExitTarget.addEventListener('pagehide', () => finish('unresolved', unresolved('page_exit')))
      }
      const soft = seed.clockId === claimedAt.clockId
      // Different documents only share wall-clock time.
      const sourceToCopilot = soft
        ? claimedAt.monotonicMs - seed.monotonicMs
        : claimedAt.observedAtMs - seed.observedAtMs
      journey = {
        openId,
        source: handoffSource(seed.source),
        submit: seed.behavior !== 'prefill',
        navigationType: soft ? 'soft' : 'hard',
        ...(sourceToCopilot >= 0 ? {sourceToCopilotMs: sourceToCopilot} : {}),
        claimedAt,
        surface,
      }
    },
    sourceFailed(seed: UiReadinessHandoff) {
      if (journey?.openId === seed.clientOpenId) {
        finish('failure', failed('handoff'))
        return
      }
      emit({
        name: 'ONE_COPILOT_WEB_HANDOFF_OUTCOME',
        value: 1,
        tags: {...tags(undefined, handoffSource(seed.source)), outcome: 'failure', ...failed('handoff')},
      })
    },
    sourceCancelled(seed: UiReadinessHandoff) {
      if (journey?.openId === seed.clientOpenId) {
        finish('unresolved', unresolved('cancelled'))
        return
      }
      emit({
        name: 'ONE_COPILOT_WEB_HANDOFF_OUTCOME',
        value: 1,
        tags: {
          ...tags(undefined, handoffSource(seed.source)),
          outcome: 'unresolved',
          ...unresolved('cancelled'),
        },
      })
    },
    observe(fact: UiReadinessFact, context?: HandoffReadinessContext) {
      const current = journey
      if (!current) return
      if (fact.client_open_id === current.openId) {
        remember(current, fact)
        if (fact.observation === 'input_ready' && !current.submit) {
          succeed(fact, [['copilot_ready', fact.monotonic_ms - current.claimedAt.monotonicMs]])
        } else if (fact.observation === 'failure') {
          finish('failure', failed(readinessFailureReason(fact.reason)), fact)
        } else if (fact.observation === 'view_disposed' && fact.reason === 'closed' && !current.submit) {
          finish('unresolved', unresolved('disposed'), fact)
        }
        return
      }
      if (
        current.submit &&
        current.taskOpenId === undefined &&
        fact.observation === 'open' &&
        TASK_SURFACES.includes(fact.surface) &&
        fact.clock_id === current.claimedAt.clockId
      ) {
        if (context?.userInitiated) {
          finish('unresolved', unresolved('cancelled'), fact)
          return
        }
        const taskId = context?.open.expectedTaskId ?? fact.task_id
        if (current.taskId && taskId && taskId !== current.taskId) return
        current.taskOpenId = fact.client_open_id
        current.taskOpenedAt = fact.monotonic_ms
        current.taskId ??= taskId
        current.surface = fact.surface
        remember(current, taskId && !fact.task_id ? {...fact, task_id: taskId} : fact)
        return
      }
      if (fact.client_open_id !== current.taskOpenId || current.taskOpenedAt === undefined) return
      if (current.taskId && fact.task_id && fact.task_id !== current.taskId) return
      remember(current, fact)
      if (fact.observation === 'input_ready') {
        succeed(fact, [
          ['copilot_to_task', current.taskOpenedAt - current.claimedAt.monotonicMs],
          ['task_ready', fact.monotonic_ms - current.taskOpenedAt],
        ])
      } else if (fact.observation === 'failure') {
        finish('failure', failed(readinessFailureReason(fact.reason)), fact)
      } else if (fact.observation === 'view_disposed' && fact.reason === 'closed') {
        finish('unresolved', unresolved('disposed'), fact)
      } else if (fact.observation === 'view_disposed' && fact.reason === 'replaced') {
        current.taskOpenId = undefined
        current.taskOpenedAt = undefined
      }
    },
  }
}

export const handoffJourneyMetrics = createHandoffJourneyMetrics()
